/**
 * Business Site V2 — 统一 API 服务层
 *
 * 对接后端 92 个端点，基于后端联调清单 v1.0
 * 后端地址: http://localhost:8001  (vite proxy /api → 8001)
 * API前缀: /api/v1
 * 响应格式: ApiResponse { code, message, data, timestamp }
 */

import axios, { type AxiosRequestConfig } from 'axios'
import type { ApiResponse, PaginatedResponse } from '../types/common'

// ── Token key（与 auth-service.ts 统一） ──
const TOKEN_KEY_ACCESS = 'bs2_access_token'
const TOKEN_KEY_REFRESH = 'bs2_refresh_token'

// ── Axios 实例 ──

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 120000,
  headers: { 'Content-Type': 'application/json' },
})

// ── 请求拦截器：自动注入 token ──

api.interceptors.request.use((config) => {
  // 三级 token 回退: bs2_ → 全局 access_token → 外部 dt_
  const token = localStorage.getItem(TOKEN_KEY_ACCESS) || localStorage.getItem('access_token') || localStorage.getItem('dt_auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
    // 统一 business_id 字段名
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
      const bizId = payload.business_id || payload.businessId || payload.enterprise_id || payload.sub || ''
      if (bizId) {
        localStorage.setItem('business_id', String(bizId))
        config.headers['X-Business-ID'] = String(bizId)
      }
      // 🔍 诊断日志：暴露 JWT payload 关键字段（联调阶段保留，上线前移除）
      if (config.url?.includes('generate') || config.url?.includes('site/')) {
        console.log('[API Auth] JWT payload 摘要:', {
          sub: payload.sub,
          role: payload.role,
          business_id: payload.business_id ?? '(空)',
          platform: payload.platform,
          bizId_injected: bizId || '(未注入)',
          url: config.url,
        })
      }
    } catch { /* token 格式异常忽略 */ }
  }
  config.headers['X-Platform'] = 'business-site'
  return config
})

// ── 响应拦截器：统一解包 ApiResponse + 401 自动刷新 ──

api.interceptors.response.use(
  (response) => {
    // 非 JSON 响应（text/stream/blob/arraybuffer）原样返回，不做 ApiResponse 解包。
    // 例如 preview-render 返回原始 HTML 文本、文件导出返回 blob、热词返回 stream。
    const responseType = response.config?.responseType
    if (responseType && responseType !== 'json') {
      return response.data
    }
    const body = response.data as ApiResponse
    if (body.code !== 0) {
      return Promise.reject(new Error(body.message || '请求失败'))
    }
    return body.data
  },
  async (error) => {
    const originalRequest = error.config
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      try {
      const refreshToken = localStorage.getItem(TOKEN_KEY_REFRESH) || localStorage.getItem('refresh_token')
      if (refreshToken) {
        const { data } = await axios.post<ApiResponse<{ access_token: string; refresh_token: string }>>(
            '/api/v1/auth/token/refresh',
            { refresh_token: refreshToken },
            { timeout: 5000 },
          )
          if (data.code === 0 && data.data) {
            localStorage.setItem(TOKEN_KEY_ACCESS, data.data.access_token)
            localStorage.setItem(TOKEN_KEY_REFRESH, data.data.refresh_token)
            originalRequest.headers.Authorization = `Bearer ${data.data.access_token}`
            return api(originalRequest)
          }
        }
      } catch {
        // 刷新失败，清除 token
      }
      localStorage.removeItem(TOKEN_KEY_ACCESS)
      localStorage.removeItem(TOKEN_KEY_REFRESH)
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
    }
    const status = error.response?.status
    const responseData = error.response?.data
    // 联调阶段：打印所有非 2xx 响应的完整请求信息 + 响应体，便于定位后端报错
    if (status && status >= 400 && responseData) {
      console.error(`[API ${status}]`, error.config?.method?.toUpperCase(), error.config?.url, responseData)
    }
    // 文本类响应体（如 preview-render 失败返回 JSON 字符串）尝试解析出真实错误信息
    let errMsg: string
    if (typeof responseData === 'string') {
      try {
        const parsed = JSON.parse(responseData) as Record<string, unknown>
        errMsg = (typeof parsed.detail === 'string' && parsed.detail)
          || (typeof parsed.message === 'string' && parsed.message)
          || responseData
      } catch {
        errMsg = responseData || error.message || '网络异常'
      }
    } else {
      errMsg = responseData?.message || responseData?.detail || error.message || '网络异常'
    }
    return Promise.reject(new Error(errMsg))
  },
)

// ── snake_case → camelCase 转换 ──

function toCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

export function keysToCamel<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(keysToCamel) as unknown as T
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [toCamel(k), keysToCamel(v)]),
    ) as T
  }
  return obj
}

function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

export function keysToSnake<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(keysToSnake) as unknown as T
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [camelToSnake(k), keysToSnake(v)]),
    ) as T
  }
  return obj
}

// ── 通用请求方法 ──

export async function get<T = unknown>(url: string, params?: Record<string, unknown>): Promise<T> {
  const result = await api.get<unknown>(url, { params })
  return keysToCamel(result) as T
}

export async function post<T = unknown>(url: string, body?: unknown, signal?: AbortSignal, timeout?: number): Promise<T> {
  const config: AxiosRequestConfig = {}
  if (signal) config.signal = signal
  if (timeout !== undefined) config.timeout = timeout
  const result = await api.post<unknown>(url, body ? keysToSnake(body) : undefined, config)
  return keysToCamel(result) as T
}

export async function put<T = unknown>(url: string, body?: unknown): Promise<T> {
  const result = await api.put<unknown>(url, body ? keysToSnake(body) : undefined)
  return keysToCamel(result) as T
}

export async function patch<T = unknown>(url: string, body?: unknown): Promise<T> {
  const result = await api.patch<unknown>(url, body ? keysToSnake(body) : undefined)
  return keysToCamel(result) as T
}

export async function del<T = unknown>(url: string, body?: Record<string, unknown>): Promise<T> {
  const result = await api.delete<unknown>(url, body ? { data: body } : undefined)
  return keysToCamel(result) as T
}

// ── 分页请求 ──

export async function getPaginated<T = unknown>(
  url: string,
  page = 1,
  pageSize = 20,
  extraParams?: Record<string, unknown>,
): Promise<PaginatedResponse<T>> {
  const params = { page, page_size: pageSize, ...extraParams }
  const result = await api.get<unknown>(url, { params })
  return keysToCamel(result) as PaginatedResponse<T>
}

export default api
